import { useNavigate, useSearch } from '@tanstack/react-router';
import { CalendarClock, Download, Save, Trash2 } from 'lucide-react';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import {
  DEFAULT_REPORT_PERIOD,
  DEFAULT_SCHEDULE_WEEKDAY,
  SCHEDULE_FREQUENCIES,
  SCHEDULE_FREQUENCY_LABELS,
  WEEKDAY_LABELS,
  analyticsReportInfo,
  type AnalyticsReport,
  type ReportColumn,
  type ReportLibrary,
  type ReportSchedule,
  type ReportTable,
  type ReportTableRow,
  type SavedReportView,
  type ScheduleFrequency,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { DataTable } from '../../../components/data-table/DataTable';
import { sortRows, type SortReaders, type SortValue } from '../../../components/data-table/sort-rows';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { MultiSelect, Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { TableSection } from '../../../components/TableSection';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime, formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { router } from '../../../router';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { DeepLink } from '../../notifications/components/DeepLink';
import {
  analyticsDrillHref,
  analyticsUrlOf,
  exportHref,
  useDeleteSavedReport,
  useReportLibrary,
  useReportTable,
  useSaveReport,
  useSavedReport,
  useUpdateSavedReport,
  type AnalyticsUrl,
  type BuilderUrl,
} from '../api/analytics-api';
import { AnalyticsFilters } from '../components/AnalyticsFilters';
import { DictionaryLine, HowCounted } from '../components/AnalyticsBits';
import { percent } from '../report-format';
import styles from '../Reports.module.css';

const NOT_SCHEDULED = '';

/**
 * RPT-06 — the report builder (master plan S29, F17.04–F17.06, DECISIONS-LOG S29–S32 Q7, Q9): pick a
 * defined report, narrow it, choose its columns, download it as Excel or CSV (masked exactly like
 * the screen), save it — privately, or shared by the HR Lead — and have it emailed on a schedule.
 */
export function ReportBuilderPage(): ReactNode {
  const canView = useCanView('reports_own');
  const search: BuilderUrl = useSearch({ from: '/app-layout/reports/builder' });
  const navigate = useNavigate();
  const library = useReportLibrary();
  const saved = useSavedReport(search.saved);

  // A saved report opens with its own filters and columns; from then on the address leads.
  const savedView = saved.data;
  const hasFilters = search.period !== undefined;
  useEffect(() => {
    if (!savedView || hasFilters) return;
    void navigate({ to: '/reports/builder', search: { saved: savedView.id, report: savedView.report, ...savedFilters(savedView), ...(savedView.columns.length > 0 ? { columns: savedView.columns.join(',') } : {}) }, replace: true });
  }, [savedView, hasFilters, navigate]);

  const report = search.report ?? savedView?.report;
  const { report: _report, saved: _saved, columns: columnText, ...url } = search;
  const chosen = (columnText ?? '').split(',').filter(Boolean);
  const table = useReportTable(report, url, []);

  if (!canView || library.error?.status === 403) return <ForbiddenState resource="reports" contact="Your admin" />;
  if (library.error && !library.data) return <ServerErrorState onRetry={() => void library.refetch()} />;

  const go = (next: BuilderUrl): void => void navigate({ to: '/reports/builder', search: next, replace: true });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Report builder</h1>
          <p className={styles.lead}>Every report as a table you can narrow, download, save and have emailed. Downloads show only what you may see on screen.</p>
        </div>
      </header>

      <div className={styles.builder}>
        <Rail library={library.data} report={report} savedId={search.saved} />
        <div className={styles.titleBlock}>
          {!report ? (
            <Card>
              <EmptyState
                title="Pick a report"
                body="Choose one on the left — or open one you saved. Every report can be narrowed, downloaded, saved and scheduled."
                action={library.data?.reports[0] ? <DeepLink to={`/reports/builder?report=${library.data.reports[0].key}`} className={styles.linkButton}>Open {library.data.reports[0].name}</DeepLink> : null}
              />
            </Card>
          ) : (
            <Workspace
              report={report}
              table={table.data}
              isLoading={!table.data}
              error={table.error ? errorMessage(table.error) : null}
              library={library.data}
              saved={search.saved ? savedView : undefined}
              chosen={chosen}
              onFilters={(next) => go({ ...next, report, ...(search.saved ? { saved: search.saved } : {}), ...(columnText ? { columns: columnText } : {}) })}
              onColumns={(columns) => go({ ...search, report, ...(columns.length > 0 ? { columns: columns.join(',') } : { columns: undefined }) })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Rail({ library, report, savedId }: { readonly library: ReportLibrary | undefined; readonly report: AnalyticsReport | undefined; readonly savedId: string | undefined }): ReactNode {
  if (!library) {
    return (
      <Card>
        <SkeletonLines count={8} />
      </Card>
    );
  }
  return (
    <Card title="Reports">
      <ul className={styles.library}>
        {library.reports.map((info) => (
          <li key={info.key}>
            <DeepLink to={`/reports/builder?report=${info.key}`} className={styles.libraryItem} aria-current={!savedId && report === info.key ? 'page' : undefined}>
              <strong>{info.name}</strong>
              <span className={styles.cellMeta}>
                {info.code} · {info.description}
              </span>
            </DeepLink>
          </li>
        ))}
      </ul>
      <h3 className={styles.subhead}>Saved</h3>
      {library.saved.length === 0 ? (
        <p className={styles.empty}>Nothing saved yet. Open a report, set it up, then “Save”.</p>
      ) : (
        <ul className={styles.library}>
          {library.saved.map((row) => (
            <li key={row.id}>
              <DeepLink to={`/reports/builder?saved=${row.id}`} className={styles.libraryItem} aria-current={savedId === row.id ? 'page' : undefined}>
                <strong>{row.name}</strong>
                <span className={styles.cellMeta}>
                  {analyticsReportInfo(row.report).name}
                  {row.isShared ? ` · shared by ${row.ownerName}` : ''}
                  {row.schedule ? ` · ${scheduleText(row.schedule).toLowerCase()}` : ''}
                </span>
              </DeepLink>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Workspace({
  report,
  table,
  isLoading,
  error,
  library,
  saved,
  chosen,
  onFilters,
  onColumns,
}: {
  readonly report: AnalyticsReport;
  readonly table: ReportTable | undefined;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly library: ReportLibrary | undefined;
  readonly saved: SavedReportView | undefined;
  readonly chosen: readonly string[];
  readonly onFilters: (next: AnalyticsUrl) => void;
  readonly onColumns: (columns: readonly string[]) => void;
}): ReactNode {
  const info = analyticsReportInfo(report);
  if (error && !table) return <ServerErrorState onRetry={() => onFilters({})} />;
  if (isLoading || !table) {
    return (
      <TableSection title={info.name}>
        <SkeletonLines count={8} />
      </TableSection>
    );
  }
  const columns = chosen.length === 0 ? table.columns : chosen.flatMap((key) => table.columns.filter((column) => column.key === key));
  const url = analyticsUrlOf(table.filters);
  const keys = columns.map((column) => column.key);
  return (
    <>
      <AnalyticsFilters filters={table.filters} audience={table.audience} onChange={onFilters} hasSource={report === 'drop_off' || report === 'source_effectiveness' || report === 'funnel'} />
      <TableSection
        title={saved ? saved.name : info.name}
        meta={`${info.code} · ${formatNumber(table.rows.length)} ${table.rows.length === 1 ? 'row' : 'rows'}`}
        action={info.screen ? <DeepLink to={info.screen} className={styles.definition}>Open the full screen</DeepLink> : undefined}
      >
        <DictionaryLine filters={table.filters} version={table.dictionaryVersion} publishedOn={table.dictionaryPublishedOn} />
        <div className={styles.builderTools}>
          <Field label="Columns">
            {({ inputId }) => (
              <MultiSelect id={inputId} label="Columns" value={keys} options={table.columns.map((column) => ({ value: column.key, label: column.label }))} onChange={(values) => onColumns(values.length === table.columns.length ? [] : values)} />
            )}
          </Field>
          <span className={styles.headerActions}>
            <a className={styles.linkButton} href={exportHref(report, url, 'xlsx', chosen, saved?.id)} download>
              <Download size={14} aria-hidden /> Excel
            </a>
            <a className={styles.linkButton} href={exportHref(report, url, 'csv', chosen, saved?.id)} download>
              <Download size={14} aria-hidden /> CSV
            </a>
          </span>
        </div>
        {table.columns.some((column) => column.isRestricted) && (
          <p className={styles.meta}>
            {table.columns.filter((column) => column.isRestricted).map((column) => column.label).join(', ')} {table.columns.filter((column) => column.isRestricted).length === 1 ? 'is' : 'are'} hidden for your access and show as —, on screen and in downloads.
          </p>
        )}
        {/* Keyed by report so each report keeps its own saved column widths and sort. */}
        <ResultTable key={report} table={table} columns={columns} report={report} />
      </TableSection>
      {library && (library.can.save || saved) && <SavePanel key={saved?.id ?? report} report={report} url={url} columns={chosen} saved={saved} library={library} />}
    </>
  );
}

/** A result row and its place in the server's order — report rows carry no id of their own. */
interface ResultRow {
  readonly id: string;
  readonly row: ReportTableRow;
}

function ResultTable({ table, columns, report }: { readonly table: ReportTable; readonly columns: readonly ReportColumn[]; readonly report: AnalyticsReport }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  if (table.rows.length === 0) return <p className={styles.empty}>Nothing to show under these filters. Widen the period or clear a filter.</p>;

  const dataColumns: readonly Column<ResultRow>[] = columns.map((column, position) => ({
    id: column.key,
    header: column.label,
    isIdentity: position === 0,
    isNumeric: column.kind !== 'text',
    isSortable: true,
    cell: ({ row }) => <ResultCell row={row} column={column} isFirst={position === 0} table={table} report={report} />,
    exportValue: ({ row }) => cellText(row.cells[column.key] ?? null, column),
  }));
  const readers: SortReaders<ResultRow> = Object.fromEntries(
    columns.map((column) => [column.key, ({ row }: ResultRow): SortValue => row.cells[column.key] ?? (column.kind === 'text' ? '' : -1)]),
  );
  const rows = sortRows(
    table.rows.map((row, index) => ({ id: String(index), row })),
    sort,
    readers,
  );
  // DataTable headers are plain words, so a column's "How this is counted" moves to a line under the table.
  const defined = columns.flatMap((column) => (column.metric ? [{ key: column.key, label: column.label, metric: column.metric }] : []));

  return (
    <>
      <DataTable
        screenId={`report-builder-${report}`}
        label={analyticsReportInfo(report).name}
        columns={dataColumns}
        rows={rows}
        getRowId={(item) => item.id}
        total={rows.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={false}
        onOpenRow={({ row }) => {
          if (row.href) router.history.push(row.href);
        }}
        exportName={report}
      />
      {defined.length > 0 && (
        <p className={styles.meta}>
          How each is counted:{' '}
          {defined.map((column, index) => (
            <Fragment key={column.key}>
              {index > 0 && ' · '}
              <HowCounted metric={column.metric} label={column.label} />
            </Fragment>
          ))}
        </p>
      )}
    </>
  );
}

/** The first cell opens the row's record; any other figure with records behind it opens them. */
function ResultCell({ row, column, isFirst, table, report }: { readonly row: ReportTableRow; readonly column: ReportColumn; readonly isFirst: boolean; readonly table: ReportTable; readonly report: AnalyticsReport }): ReactNode {
  const text = cellText(row.cells[column.key] ?? null, column);
  if (text === EMPTY) return null;
  if (isFirst && row.href) {
    return (
      <DeepLink to={row.href} className={styles.rowLink}>
        {text}
      </DeepLink>
    );
  }
  const drill = row.drills[column.key];
  if (!drill || text === '0') return text;
  return (
    <DeepLink to={analyticsDrillHref(table.filters, report, drill)} className={styles.count}>
      {text}
    </DeepLink>
  );
}

/** F17.06 and F17.05: save (private, or shared by the HR Lead) and schedule. */
function SavePanel({ report, url, columns, saved, library }: { readonly report: AnalyticsReport; readonly url: AnalyticsUrl; readonly columns: readonly string[]; readonly saved: SavedReportView | undefined; readonly library: ReportLibrary }): ReactNode {
  const showToast = useToasts((state) => state.show);
  const create = useSaveReport();
  const update = useUpdateSavedReport();
  const remove = useDeleteSavedReport();
  const navigate = useNavigate();
  const isOwner = saved?.can.edit ?? false;
  const [name, setName] = useState(saved?.name ?? analyticsReportInfo(report).name);
  const [isShared, setIsShared] = useState(saved?.isShared ?? false);
  const [frequency, setFrequency] = useState<ScheduleFrequency | typeof NOT_SCHEDULED>(saved?.schedule?.frequency ?? NOT_SCHEDULED);
  const [weekday, setWeekday] = useState(saved?.schedule?.weekday ?? DEFAULT_SCHEDULE_WEEKDAY);
  const schedule: ReportSchedule | null = frequency === NOT_SCHEDULED ? null : { frequency, weekday: frequency === 'weekly' ? weekday : null };
  const isNameValid = name.trim().length >= 2;
  const filters = { ...url, period: url.period ?? DEFAULT_REPORT_PERIOD };

  const saveNew = (): void => {
    if (!isNameValid) return;
    create.mutate(
      { name: name.trim(), report, filters, columns: [...columns], isShared: library.can.share && isShared, schedule: library.can.schedule ? schedule : null },
      {
        onSuccess: (view) => {
          showToast({ tone: 'success', message: view.schedule ? `Saved. It will be emailed ${scheduleText(view.schedule).toLowerCase()} at 8am.` : 'Saved' });
          void navigate({ to: '/reports/builder', search: { saved: view.id }, replace: true });
        },
        onError: (error) => showToast({ tone: 'danger', message: "Couldn't save the report", detail: errorMessage(error) }),
      },
    );
  };
  const saveChanges = (): void => {
    if (!saved || !isNameValid) return;
    update.mutate(
      { id: saved.id, input: { version: saved.version, name: name.trim(), filters, columns: [...columns], isShared: library.can.share && isShared, schedule } },
      {
        onSuccess: () => showToast({ tone: 'success', message: 'Changes saved' }),
        onError: (error) => showToast({ tone: 'danger', message: "Couldn't save the changes", detail: errorMessage(error) }),
      },
    );
  };
  const deleteIt = (): void => {
    if (!saved) return;
    remove.mutate(
      { id: saved.id, version: saved.version },
      {
        onSuccess: () => {
          showToast({ tone: 'success', message: `“${saved.name}” deleted` });
          void navigate({ to: '/reports/builder', search: { report }, replace: true });
        },
        onError: (error) => showToast({ tone: 'danger', message: "Couldn't delete it", detail: errorMessage(error) }),
      },
    );
  };

  return (
    <Card title={saved ? (isOwner ? 'This saved report' : `Shared by ${saved.ownerName}`) : 'Save this report'} meta={saved?.lastSentAt ? `Last emailed ${formatDateTime(saved.lastSentAt)}` : undefined}>
      <form
        className={styles.titleBlock}
        onSubmit={(event) => {
          event.preventDefault();
          if (saved && isOwner) saveChanges();
          else saveNew();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            if (saved && isOwner) saveChanges();
            else saveNew();
          }
        }}
      >
        <div className={styles.formGrid}>
          <Field label="Name" isRequired error={isNameValid ? undefined : 'Give it a name of at least 2 characters.'}>
            {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />}
          </Field>
          {library.can.schedule && (
            <Field label="Email it to me" hint="At 8am, as an Excel file, built with your access at the time">
              {({ inputId }) => (
                <Select
                  id={inputId}
                  label="Email it to me"
                  value={frequency}
                  options={[{ value: NOT_SCHEDULED, label: 'Not scheduled' }, ...SCHEDULE_FREQUENCIES.map((value) => ({ value, label: SCHEDULE_FREQUENCY_LABELS[value] }))]}
                  onChange={(value) => setFrequency(SCHEDULE_FREQUENCIES.find((option) => option === value) ?? NOT_SCHEDULED)}
                />
              )}
            </Field>
          )}
          {frequency === 'weekly' && (
            <Field label="On">
              {({ inputId }) => <Select id={inputId} label="On" value={String(weekday)} options={Object.entries(WEEKDAY_LABELS).map(([value, label]) => ({ value, label }))} onChange={(value) => setWeekday(Number(value))} />}
            </Field>
          )}
          {library.can.share && <Checkbox checked={isShared} onChange={(checked) => setIsShared(checked)} label="Share with HR — everyone who may open this report sees it" />}
        </div>
        <span className={styles.headerActions}>
          {saved && isOwner ? (
            <>
              <Button type="submit" variant="solid" icon={Save} isPending={update.isPending} disabled={!isNameValid}>
                Save changes
              </Button>
              <Button type="button" variant="outline" icon={CalendarClock} onClick={saveNew} disabled={!isNameValid}>
                Save as new
              </Button>
              <Button type="button" variant="danger" icon={Trash2} isPending={remove.isPending} onClick={deleteIt}>
                Delete
              </Button>
            </>
          ) : (
            library.can.save && (
              <Button type="submit" variant="solid" icon={Save} isPending={create.isPending} disabled={!isNameValid}>
                {saved ? 'Save a copy for me' : 'Save'}
              </Button>
            )
          )}
        </span>
      </form>
    </Card>
  );
}

function cellText(value: string | number | null, column: ReportColumn): string {
  if (value === null || value === '') return EMPTY;
  if (typeof value === 'string') return value;
  if (column.kind === 'percent') return percent(value);
  if (column.kind === 'count') return formatNumber(value);
  return String(value);
}

function scheduleText(schedule: ReportSchedule): string {
  if (schedule.frequency === 'weekly') return `Every ${WEEKDAY_LABELS[schedule.weekday ?? DEFAULT_SCHEDULE_WEEKDAY] ?? ''}`;
  return SCHEDULE_FREQUENCY_LABELS[schedule.frequency];
}

function savedFilters(view: SavedReportView): AnalyticsUrl {
  const filters = view.filters;
  return {
    period: filters.period,
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.ventureId ? { ventureId: filters.ventureId } : {}),
    ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
    ...(filters.recruiterUserId ? { recruiterUserId: filters.recruiterUserId } : {}),
    ...(filters.roleFamily ? { roleFamily: filters.roleFamily } : {}),
    ...(filters.jobId ? { jobId: filters.jobId } : {}),
    ...(filters.source ? { source: filters.source } : {}),
  };
}
